import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchAlphaLeaderboard, AlphaLeaderboardRow } from "../api/client";
import { TpeSearchModal } from "./TpeSearchModal";

/**
 * Per-ticker alpha leaderboard for one TPE run.
 *
 * The headline column is EXPOSURE-MATCHED excess (`em_excess_pct`) — genuine
 * timing alpha vs holding the same average exposure passively, so a strong
 * rally can't flatter a beta-tracker and can't unfairly sink a lower-beta
 * timer. Positive = beat passive (blue), negative = lagged (orange).
 *
 * Colour is only a reinforcement: every value is shown with an explicit sign,
 * and promotion is a text chip — never colour alone (protan-CVD-safe; no
 * red/green, anchored on blue↔orange).
 */

const BLUE = "#3b82f6";   // positive alpha / "good"
const ORANGE = "#f59e0b"; // negative alpha / "lagged"

function fmtPct(v: number | null | undefined, dp = 2): string {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  const s = v >= 0 ? "+" : "−";
  return `${s}${Math.abs(v).toFixed(dp)}%`;
}

function AlphaBar({ value }: { value: number }) {
  // Diverging bar centered at 0; blue grows right (positive), orange left.
  // Scale: clamp to ±3% for width (alpha is typically small).
  const mag = Math.min(Math.abs(value), 3) / 3; // 0..1
  const pos = value >= 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ position: "relative", width: 120, height: 12, background: "#0f172a",
                    borderRadius: 3, border: "1px solid #1e293b" }}>
        <div style={{ position: "absolute", left: "50%", top: 0, bottom: 0, width: 1, background: "#334155" }} />
        <div style={{
          position: "absolute", top: 1, bottom: 1,
          ...(pos ? { left: "50%" } : { right: "50%" }),
          width: `${mag * 50}%`,
          background: pos ? BLUE : ORANGE, borderRadius: 2,
        }} />
      </div>
      <span style={{ color: pos ? BLUE : ORANGE, fontVariantNumeric: "tabular-nums", minWidth: 64 }}>
        {fmtPct(value)}
      </span>
    </div>
  );
}

export function AlphaLeaderboardPage() {
  const [params, setParams] = useSearchParams();
  const urlRun = params.get("run_id") ?? "";
  const [runId, setRunId] = useState(urlRun);
  const [submitted, setSubmitted] = useState(urlRun);
  const [modalOpen, setModalOpen] = useState(false);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["alpha-leaderboard", submitted],
    queryFn: () => fetchAlphaLeaderboard(submitted),
    enabled: !!submitted,
    staleTime: 30_000,
  });

  const summary = useMemo(() => {
    const rows = data?.tickers ?? [];
    const positive = rows.filter((r) => r.em_excess_pct > 0).length;
    const promoted = rows.filter((r) => r.promoted).length;
    const best = rows.length ? rows[0] : null; // backend sorts desc by em_excess
    return { total: rows.length, positive, promoted, best };
  }, [data]);

  const go = () => {
    const v = runId.trim();
    setSubmitted(v);
    if (v) setParams({ run_id: v }); else setParams({});
  };

  const onLaunched = (newRunId: string) => {
    setRunId(newRunId);
    setSubmitted(newRunId);
    setParams({ run_id: newRunId });
  };

  return (
    <div>
      <TpeSearchModal open={modalOpen} onClose={() => setModalOpen(false)} onLaunched={onLaunched} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={{ marginTop: 0 }}>Alpha leaderboard</h2>
        <button onClick={() => setModalOpen(true)} style={{
          background: BLUE, color: "#04122e", border: "none", borderRadius: 4,
          padding: "8px 14px", fontWeight: 600, cursor: "pointer",
        }}>⚙ Configure &amp; launch search</button>
      </div>

      <div className="card" style={{
        background: "#0c1e3d", border: "1px solid #1e40af", borderLeft: `4px solid ${BLUE}`,
        fontSize: 13, lineHeight: 1.55, color: "#e2e8f0",
      }}>
        <p style={{ marginTop: 0 }}>
          Per-ticker <strong>exposure-matched alpha</strong> for a run: each ticker's best Phase-2
          config (mean over replicates), ranked by <strong>em-alpha</strong> = return minus a
          buy-and-hold benchmark <em>scaled to the agent's own average exposure</em>. That credits
          <strong> timing of exposure</strong>, not how trendy the stock was — so a 50%-invested
          model that beats holding 50% scores positive even on a rally it can't out-return outright.
        </p>
        <p style={{ marginBottom: 0 }}>
          <span style={{ color: BLUE }}>Blue / +</span> = beat its exposure-matched passive
          (real timing alpha). <span style={{ color: ORANGE }}>Orange / −</span> = lagged it.
          <em> Raw excess</em> (vs 100%-long buy-hold) is shown for context — it punishes any
          under-investment on an up move, which is exactly why it's the wrong yardstick.
        </p>
      </div>

      <div className="card" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <label style={{ color: "#94a3b8", fontSize: 12 }}>Run ID</label>
        <input
          value={runId}
          onChange={(e) => setRunId(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") go(); }}
          placeholder="20260602_095518_301"
          style={{ background: "#0f172a", color: "#e2e8f0", border: "1px solid #334155",
                   borderRadius: 4, padding: "6px 10px", fontSize: 13, minWidth: 260,
                   fontFamily: "monospace" }}
        />
        <button onClick={go} style={{ background: "#1e3a8a", color: "#bfdbfe",
          border: "1px solid #334155", borderRadius: 4, padding: "6px 14px", fontSize: 13, cursor: "pointer" }}>
          Load
        </button>
        {submitted && (
          <button onClick={() => refetch()} disabled={isFetching}
            style={{ background: "#0f172a", color: "#cbd5e1", border: "1px solid #334155",
                     borderRadius: 4, padding: "6px 12px", fontSize: 12, cursor: "pointer" }}>
            {isFetching ? "Refreshing…" : "Refresh"}
          </button>
        )}
      </div>

      {error && (
        <div className="card" style={{ color: ORANGE }}>
          Failed to load: {String((error as Error).message || error)}
        </div>
      )}

      {!submitted && (
        <div className="card" style={{ color: "#94a3b8" }}>
          Enter a run ID to see its per-ticker alpha. (Phase-2 rows appear as the run progresses —
          re-load to refresh while it's still running.)
        </div>
      )}

      {submitted && data && data.tickers.length === 0 && (
        <div className="card" style={{ color: "#94a3b8" }}>
          {data.note ?? "No Phase-2 rows for this run yet."}
        </div>
      )}

      {data && data.tickers.length > 0 && (
        <>
          <div className="card" style={{ display: "flex", gap: 24, flexWrap: "wrap", fontSize: 13 }}>
            <div><span style={{ color: "#94a3b8" }}>Tickers</span> <strong>{summary.total}</strong></div>
            <div><span style={{ color: "#94a3b8" }}>Positive alpha</span>{" "}
              <strong style={{ color: summary.positive ? BLUE : "#cbd5e1" }}>{summary.positive}</strong>/{summary.total}</div>
            <div><span style={{ color: "#94a3b8" }}>Promoted</span> <strong>{summary.promoted}</strong></div>
            {summary.best && (
              <div><span style={{ color: "#94a3b8" }}>Best</span>{" "}
                <strong>{summary.best.ticker}</strong>{" "}
                <span style={{ color: summary.best.em_excess_pct >= 0 ? BLUE : ORANGE }}>
                  {fmtPct(summary.best.em_excess_pct)}
                </span></div>
            )}
          </div>

          <div className="card" style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>Ticker</th>
                  <th style={{ textAlign: "left" }} title="Exposure-matched excess CAGR — the trend-neutral timing alpha. Ranked on this.">em-alpha (timing)</th>
                  <th className="num" title="Average net exposure of the winning config (1.0 = fully invested).">avg expo</th>
                  <th className="num" title="Agent CAGR vs buy-and-hold CAGR over the OOS window.">agent / b&h cagr</th>
                  <th className="num" title="Raw excess vs 100%-long buy-and-hold (penalizes under-investment on up moves).">raw excess</th>
                  <th className="num" title="Avg trades / replicates / distinct configs tried.">trades · reps · cfgs</th>
                  <th style={{ textAlign: "left" }}>promoted</th>
                </tr>
              </thead>
              <tbody>
                {data.tickers.map((r: AlphaLeaderboardRow) => (
                  <tr key={r.ticker}>
                    <td style={{ fontWeight: 600 }}>{r.ticker}</td>
                    <td><AlphaBar value={r.em_excess_pct} /></td>
                    <td className="num" style={{ fontVariantNumeric: "tabular-nums" }}>
                      {r.avg_net_exposure === null ? "—" : r.avg_net_exposure.toFixed(2)}
                    </td>
                    <td className="num" style={{ fontVariantNumeric: "tabular-nums" }}>
                      {r.agent_cagr_pct === null ? "—" : `${r.agent_cagr_pct.toFixed(0)}%`}
                      <span style={{ color: "#64748b" }}> / {r.benchmark_cagr_pct === null ? "—" : `${r.benchmark_cagr_pct.toFixed(0)}%`}</span>
                    </td>
                    <td className="num" style={{ color: ORANGE, fontVariantNumeric: "tabular-nums" }}>
                      {fmtPct(r.raw_excess_pct)}
                    </td>
                    <td className="num" style={{ color: "#94a3b8", fontVariantNumeric: "tabular-nums" }}>
                      {r.avg_num_trades === null ? "—" : Math.round(r.avg_num_trades)} · {r.n_replicates} · {r.n_configs}
                    </td>
                    <td>
                      {r.promoted
                        ? <span style={{ background: "#0c1e3d", color: BLUE, border: `1px solid ${BLUE}`,
                                         borderRadius: 4, padding: "2px 8px", fontSize: 12 }}>✓ promoted</span>
                        : <span style={{ color: "#64748b" }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {data && data.excluded && data.excluded.length > 0 && (
        <div className="card" style={{ borderLeft: `4px solid ${ORANGE}` }}>
          <div style={{ fontSize: 13, color: "#e2e8f0", marginBottom: 6 }}>
            <strong>Excluded for data sanity</strong> ({data.excluded.length}) — pulled from the ranking,
            not scored. These have implausible benchmark CAGR (bad/sandbox price data) or near-cash
            exposure (which inflates exposure-matched alpha). Fix the data before trusting them.
          </div>
          <table style={{ width: "100%", fontSize: 12.5 }}>
            <tbody>
              {data.excluded.map((r) => (
                <tr key={r.ticker}>
                  <td style={{ fontWeight: 600, width: 70 }}>{r.ticker}</td>
                  <td className="num" style={{ width: 110, color: "#64748b" }}>
                    b&h cagr {r.benchmark_cagr_pct === null ? "—" : `${r.benchmark_cagr_pct.toFixed(0)}%`}
                  </td>
                  <td className="num" style={{ width: 80, color: "#64748b" }}>
                    expo {r.avg_net_exposure === null ? "—" : r.avg_net_exposure.toFixed(2)}
                  </td>
                  <td style={{ color: ORANGE }}>{r.exclude_reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isLoading && submitted && (
        <div className="card" style={{ color: "#94a3b8" }}>Loading…</div>
      )}
    </div>
  );
}
